import { useAuth } from "@/features/auth";
import { IntervalsICUCard } from "@/features/settings";
import { Button } from "@/shared/ui/button";

interface SettingsPageProps {
  onBack: () => void;
}

export function SettingsPage({ onBack }: SettingsPageProps) {
  const { user } = useAuth();

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background">
      {/* Background layers */}
      <div className="absolute inset-0">
        {/* Radial gradient - creates depth and focus */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(
                ellipse 80% 60% at 50% 30%,
                oklch(0.24 0.02 55 / 0.3) 0%,
                transparent 70%
              )
            `,
          }}
        />

        {/* Diagonal lines pattern - athletic motif */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              var(--foreground) 0,
              var(--foreground) 1px,
              transparent 1px,
              transparent 60px
            )`,
          }}
        />

        {/* Ambient glow at bottom */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px]"
          style={{
            background: `
              radial-gradient(
                ellipse at center bottom,
                oklch(0.75 0.15 55 / 0.06) 0%,
                transparent 70%
              )
            `,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header
          className="shrink-0 h-14 border-b border-border/50 px-6 flex items-center gap-4
                     animate-in fade-in slide-in-from-top-2 duration-500"
          style={{ animationFillMode: "backwards" }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-foreground-muted hover:text-foreground -ml-2"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-4 h-4 mr-1.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back
          </Button>
          <div className="h-4 w-px bg-border/50" />
          <h1 className="text-sm font-medium tracking-wide text-foreground/80">
            Settings
          </h1>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-lg mx-auto px-6 py-10">
            {/* Profile section */}
            <section
              className="mb-10 animate-in fade-in slide-in-from-bottom-4 duration-700"
              style={{ animationDelay: "100ms", animationFillMode: "backwards" }}
            >
              <h2 className="text-xs font-medium uppercase tracking-wider text-foreground-muted mb-4">
                Profile
              </h2>
              <div className="relative group">
                <div
                  className="relative bg-card/60 backdrop-blur-sm border border-border
                             rounded-xl p-5"
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div
                      className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20
                                 flex items-center justify-center"
                    >
                      <span className="text-lg font-medium text-primary">
                        {user?.name?.charAt(0).toUpperCase() || "?"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-medium text-foreground truncate">
                        {user?.name || "Unknown"}
                      </p>
                      <p className="text-sm text-foreground-muted truncate">
                        {user?.email || "No email"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Integrations section */}
            <section
              className="animate-in fade-in slide-in-from-bottom-4 duration-700"
              style={{ animationDelay: "200ms", animationFillMode: "backwards" }}
            >
              <h2 className="text-xs font-medium uppercase tracking-wider text-foreground-muted mb-4">
                Integrations
              </h2>
              <IntervalsICUCard />
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer
          className="shrink-0 py-4 text-center animate-in fade-in duration-700"
          style={{ animationDelay: "400ms", animationFillMode: "backwards" }}
        >
          <p className="text-xs text-foreground-subtle">
            Koach &middot; Your AI training companion
          </p>
        </footer>
      </div>
    </div>
  );
}
